/**
 * `src/spa/registry.ts` — turn the tenant's `resolve` payload into single-spa
 * registrations, backed by `System.import()` against the injected import map.
 */
import { registerApplication, type AppProps, type LifeCycles } from "single-spa";
import type { ImportMapEntry, ResolvedMfe } from "@/tenant/types";
import { routeUnderPrefix } from "@/nav/navFilter";
import { installModuleLoadGuards, loadTenantModule, rememberModuleRole } from "@/spa/moduleLoadFailure";

export function buildImportMap(entries: ImportMapEntry[]): { imports: Record<string, string> } {
  return { imports: Object.fromEntries(entries.map((e) => [e.key, e.url])) };
}

/** Dev-only: remembers `?mfe=` overrides for the rest of the tab's life (same shape as `?tenantHost=`). */
const DEV_MFE_OVERRIDES_KEY = "metaship:dev:mfeOverrides";

/**
 * Dev-only (DEV-923): point one or more modules at a LOCAL build instead of
 * the URL the tenant's import map carries — `?mfe=@metaship/boer=http://localhost:8116/spa.js`,
 * repeatable. The override is remembered per tab so it survives the sign-in
 * round-trip through Auth0, and it never runs on a real tenant hostname:
 * `main.ts` gates the call behind `isDevHost()` exactly as it does the tenant
 * host override, because an import-map override on a live domain would be a
 * script-injection primitive.
 */
export function applyDevImportMapOverrides(
  entries: ImportMapEntry[],
  search: string = window.location.search,
): ImportMapEntry[] {
  const overrides = new Map<string, string>();
  try {
    const remembered = sessionStorage.getItem(DEV_MFE_OVERRIDES_KEY);
    for (const [key, url] of Object.entries(remembered ? (JSON.parse(remembered) as Record<string, string>) : {})) {
      overrides.set(key, url);
    }
  } catch {
    // best-effort — a remembered override just stops surviving the redirect
  }
  for (const raw of new URLSearchParams(search).getAll("mfe")) {
    const eq = raw.indexOf("=");
    if (eq > 0) overrides.set(raw.slice(0, eq), raw.slice(eq + 1));
  }
  if (!overrides.size) return entries;
  try {
    sessionStorage.setItem(DEV_MFE_OVERRIDES_KEY, JSON.stringify(Object.fromEntries(overrides)));
  } catch {
    /* see above */
  }
  return entries.map((e) => (overrides.has(e.key) ? { ...e, url: overrides.get(e.key) as string } : e));
}

/** Append the `<script type="systemjs-importmap">` SystemJS reads before any `System.import()` call. */
export function injectImportMap(entries: ImportMapEntry[], doc: Document = document): void {
  const script = doc.createElement("script");
  script.type = "systemjs-importmap";
  script.textContent = JSON.stringify(buildImportMap(entries));
  doc.head.appendChild(script);
}

type MfeMatch = Pick<ResolvedMfe, "pathPrefix" | "matchMode" | "overlay">;

/** Overlays (comms, help-center) mount on every route; everything else matches its prefix (or exact path). */
export function activeWhenFor(mfe: MfeMatch): (location: Pick<Location, "pathname">) => boolean {
  if (mfe.overlay) return () => true;
  if (mfe.matchMode === "exact") {
    return (location) => location.pathname === mfe.pathPrefix;
  }
  return (location) => routeUnderPrefix(location.pathname, mfe.pathPrefix);
}

/**
 * `System.import` (populated by the `systemjs` side-effect import in
 * `main.ts`) resolves to the module's single-spa lifecycle object directly —
 * it is NOT itself a further loading function, so this loader's signature
 * matches single-spa's `(config: AppProps) => Promise<LifeCycles>` contract.
 *
 * A rejected import is recorded by `loadTenantModule` (INS-9) before it is
 * rethrown, so single-spa still parks the app in `LOAD_ERROR`.
 */
function loadMfe(importKey: string, overlay: boolean): (config: AppProps) => Promise<LifeCycles> {
  return () => loadTenantModule(importKey, overlay);
}

/** Register every resolved module (and only those) as a single-spa application. */
export function registerTenantModules(modules: ResolvedMfe[]): void {
  // Before any `registerApplication` — single-spa may load an active app
  // synchronously on register, and that import can already be the failure.
  installModuleLoadGuards();
  for (const mfe of modules) {
    rememberModuleRole(mfe.importKey, mfe.overlay);
    registerApplication({
      name: mfe.importKey,
      app: loadMfe(mfe.importKey, mfe.overlay),
      activeWhen: activeWhenFor(mfe),
    });
  }
}
